import { useEffect, useState, useRef } from "react";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { exportElementToPdf } from "@/lib/exportPdf";
import { useMetrics } from "@/context/MetricsContext";
import { metricColor, metricAverage, dayAverage, average, scoreColor, scoreTint, scoreBorder } from "@/lib/grades";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from "recharts";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function AnnualView({ patientId }) {
  const metrics = useMetrics();
  const [year, setYear] = useState(dayjs().year());
  const [entries, setEntries] = useState([]);
  const reportRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      await exportElementToPdf(reportRef.current, `AnnieHealth-Annual-${year}.pdf`);
    } catch (err) {
      toast.error("Could not generate PDF");
    } finally {
      setExporting(false);
    }
  };

  const start = `${year}-01-01`;
  const end = `${year}-12-31`;

  useEffect(() => {
    const params = { start, end };
    if (patientId) params.patient_id = patientId;
    api.get("/entries", { params }).then((r) => setEntries(r.data)).catch(() => setEntries([]));
  }, [start, end, patientId]);

  const monthly = MONTHS.map((label, idx) => {
    const me = entries.filter((e) => dayjs(e.date).month() === idx);
    const row = { month: label, idx, count: me.length, overall: average(me.map((e) => dayAverage(e))) };
    metrics.forEach((m) => (row[m.key] = metricAverage(me, m.key)));
    return row;
  });

  const logged = monthly.filter((m) => m.count > 0);
  const best = logged.length ? logged.reduce((a, b) => (b.overall > a.overall ? b : a)) : null;

  return (
    <div ref={reportRef} data-testid="annual-view" className="animate-fade-up space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-xl sm:text-2xl font-serif">{year} overview</h3>
        <div className="flex gap-2 no-export">
          <Button variant="outline" className="rounded-full" data-testid="annual-export-pdf"
            onClick={handleExport} disabled={exporting}>
            <Download className="h-4 w-4 mr-1.5" /> {exporting ? "Exporting…" : "Export PDF"}
          </Button>
          <Button variant="outline" size="icon" className="rounded-full" data-testid="annual-prev"
            onClick={() => setYear(year - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" className="rounded-full" data-testid="annual-next"
            onClick={() => setYear(year + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="annual-averages">
        {metrics.map((m) => {
          const a = metricAverage(entries, m.key);
          return (
            <Card key={m.key} className="p-6 rounded-2xl border-border/70 flex flex-col items-center text-center">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{m.label}</div>
              <div className="text-5xl font-serif font-bold"
                style={{ color: a != null ? scoreColor(a) : undefined }} data-testid={`annual-avg-${m.key}`}>
                {a != null ? a.toFixed(1) : "–"}
              </div>
            </Card>
          );
        })}
        <Card className="p-6 rounded-2xl border-border/70 flex flex-col items-center text-center">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Total entries</div>
          <div className="text-5xl font-serif font-bold" data-testid="annual-total">{entries.length}</div>
        </Card>
      </div>

      <Card className="p-5 sm:p-6 rounded-2xl border-border/70">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Monthly average by metric</div>
          {best && (
            <div className="text-xs text-muted-foreground" data-testid="annual-best">
              Best month: <span className="font-semibold text-foreground">{MONTHS[best.idx]}</span>
            </div>
          )}
        </div>
        <div style={{ width: "100%", height: 280 }} data-testid="annual-chart">
          <ResponsiveContainer>
            <LineChart data={monthly} margin={{ top: 5, right: 10, left: -22, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis domain={[1, 10]} ticks={[2, 4, 6, 8, 10]} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))" }} />
              <Legend />
              {metrics.map((m, idx) => (
                <Line key={m.key} type="monotone" dataKey={m.key} name={m.label}
                  stroke={metricColor(m.key, idx)} strokeWidth={2.5} connectNulls
                  dot={{ r: 3 }} activeDot={{ r: 5 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-5 sm:p-6 rounded-2xl border-border/70">
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-4">12-month heatmap (overall average)</div>
        <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-2" data-testid="annual-heatmap">
          {monthly.map((m) => (
            <div key={m.idx} data-testid={`annual-month-${m.idx}`}
              className="rounded-xl p-3 flex flex-col items-center gap-1 border"
              style={{ backgroundColor: scoreTint(m.overall), borderColor: scoreBorder(m.overall) }}>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{m.month}</span>
              <span className="font-mono font-bold text-base"
                style={{ color: m.overall != null ? scoreColor(m.overall) : "hsl(var(--muted-foreground))" }}>
                {m.overall != null ? m.overall.toFixed(1) : "–"}
              </span>
              <span className="text-[10px] text-muted-foreground">{m.count}d</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
